import { SearchVehiclesUnicVehicle } from '@/src/services/v2/vehicles/types';
import React, { createContext, useCallback, useContext, useState } from 'react';
import { Context } from 'src/context';
import { fetchAdminSearchVehicles, fetchAdminSearchVehiclesExport, fetchSearchVehicles, fetchSearchVehiclesExport } from 'src/services/v2/vehicles';

interface SearchVehiclesContextData {
  isLoadingItensSearchVehicles: boolean;
  itensSearchVehicles: SearchVehiclesUnicVehicle[];
  loadListSearchVehicles: (filter: any) => Promise<void>;
  loadListSearchVehiclesExport: (filter: any) => Promise<any>;
  totalItensSearchVehicles: number;
  searchFilter: string;
  typeFilter: string | null;
  setSearchFilter: (search: string) => void;
  setTypeFilter: (type: string | null) => void;
  shouldLoad: boolean;
  setShouldLoad: (shouldLoad: boolean) => void;
  isLoadingDownload: boolean;
}

const SearchVehiclesContext = createContext<SearchVehiclesContextData>({} as SearchVehiclesContextData);

export function ListSearchVehiclesProvider({ children }: { children: React.ReactNode }) {
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [shouldLoad, setShouldLoad] = useState<boolean>(false);
  const [isLoadingDownload, setIsLoadingDownload] = useState<boolean>(false);
  const [itensSearchVehicles, setItensSearchVehicles] = useState<SearchVehiclesUnicVehicle[]>([]);
  const [totalItensSearchVehicles, setTotalItensSearchVehicles] = useState<number>(0);
  const [isLoadingItensSearchVehicles, setIsLoadingItensSearchVehicles] = useState<boolean>(true);

  const { userAuth } = useContext(Context);
  const isAdmin = userAuth?.admin;

  const loadListSearchVehicles = useCallback(
    async (filter: any) => {
      try {
        setIsLoadingItensSearchVehicles(true);
        const { data } = isAdmin ? await fetchAdminSearchVehicles(filter) : await fetchSearchVehicles(filter)

        setItensSearchVehicles(data?.content || []);
        setTotalItensSearchVehicles(data?.pagination.total || 0);
      } catch (error) {
        handleGetSearchVehiclesError();
      } finally {
        setIsLoadingItensSearchVehicles(false);
      }
    },
    [userAuth?.admin],
  );

  const loadListSearchVehiclesExport = useCallback(
    async (filter: any) => {
      try {
        setIsLoadingDownload(true);
        const response = userAuth?.admin ? await fetchAdminSearchVehiclesExport(filter) : await fetchSearchVehiclesExport(filter)
        if (!response.data) return new Error();
        const url = URL.createObjectURL(
          new Blob([response?.data], {
            type: 'octet/stream',
          }),
        );
        const date = new Date();
        const fileName = `${date.toLocaleDateString()}.csv`;
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        setIsLoadingDownload(false);
      } catch (err) {
        setIsLoadingDownload(false);
      }
    },
    [userAuth?.admin],
  );

 
  const handleGetSearchVehiclesError = () => {
    setItensSearchVehicles([]);
    setTotalItensSearchVehicles(0);
  };

  return (
    <SearchVehiclesContext.Provider
      value={{
        isLoadingItensSearchVehicles,
        itensSearchVehicles,
        loadListSearchVehicles,
        loadListSearchVehiclesExport,
        isLoadingDownload,
        totalItensSearchVehicles,
        searchFilter,
        setSearchFilter,
        typeFilter,
        setTypeFilter,
        shouldLoad,
        setShouldLoad
      }}
    >
      {children}
    </SearchVehiclesContext.Provider>
  );
}

export function useListSearchVehicles() {
  const context = useContext(SearchVehiclesContext);

  if (!context) {
    throw new Error('useListSearchVehicles must be used within a SearchVehiclesProvider');
  }

  return context;
}
